import { z } from 'zod';

// Browser-safe enumerations shared by web, api and worker.
// Values mirror prisma/schema.prisma enums exactly.

export const ROLES = ['owner', 'admin', 'manager', 'advisor', 'viewer'] as const;
export const RoleSchema = z.enum(ROLES);
export type Role = z.infer<typeof RoleSchema>;

/** Role ordering used for "at least" checks (PRD §4). */
export const ROLE_RANK: Record<Role, number> = { viewer: 1, advisor: 2, manager: 3, admin: 4, owner: 5 };
export function roleAtLeast(role: Role, required: Role): boolean {
  return ROLE_RANK[role] >= ROLE_RANK[required];
}

export const PLATFORM_CAPABILITIES = ['platform_prompt_admin', 'platform_model_admin', 'platform_integration_admin', 'platform_mcp'] as const;
export const PlatformCapabilitySchema = z.enum(PLATFORM_CAPABILITIES);
export type PlatformCapability = z.infer<typeof PlatformCapabilitySchema>;

/** Spanish labels of the platform capabilities (granted in «Plataforma → Equipo Estateflow»; the first operator with `pnpm admin:bootstrap`, docs/deployment.md). */
export const PLATFORM_CAPABILITY_LABELS: Record<PlatformCapability, string> = {
  platform_prompt_admin: 'Instrucciones globales',
  platform_model_admin: 'Modelos de IA',
  platform_integration_admin: 'Integraciones de plataforma',
  platform_mcp: 'Acceso por MCP (ChatGPT)',
};

/** One-line Spanish explanation of each platform capability («Plataforma → Equipo Estateflow»). */
export const PLATFORM_CAPABILITY_DESCRIPTIONS: Record<PlatformCapability, string> = {
  platform_prompt_admin: 'Edita, publica y restaura las instrucciones globales del agente y la cola de aprendizaje.',
  platform_model_admin: 'Configura proveedores de modelos de IA y sus asignaciones por uso.',
  platform_integration_admin: 'Administra las integraciones de la plataforma, la contingencia y el reporte semanal.',
  platform_mcp: 'Puede conectar ChatGPT/Claude a LIA y configurar clientes.',
};

/**
 * A «platform super operator» holds every one of these: only they manage the Estateflow team (who holds which platform
 * capability). platform_mcp is not required.
 */
export const PLATFORM_TEAM_ADMIN_CAPABILITIES = ['platform_prompt_admin', 'platform_model_admin', 'platform_integration_admin'] as const satisfies readonly PlatformCapability[];

/**
 * Whether these capabilities make a platform super operator. The single rule shared by the API guard
 * (`platformTeamAdmin` routes), the platform-team service and the web (tab visibility).
 */
export function isPlatformTeamAdmin(capabilities: readonly string[]): boolean {
  return PLATFORM_TEAM_ADMIN_CAPABILITIES.every((c) => capabilities.includes(c));
}

export const SETUP_STEP_STATUSES = ['pending', 'in_progress', 'ready', 'skipped', 'needs_attention'] as const;
export const SetupStepStatusSchema = z.enum(SETUP_STEP_STATUSES);
export type SetupStepStatus = z.infer<typeof SetupStepStatusSchema>;

export const CONNECTION_PROVIDERS = ['kapso_whatsapp', 'meta_whatsapp', 'aircall', 'calendly', 'calcom', 'hubspot', 'highlevel', 'generic_webhook', 'telnyx', 'lia_test'] as const;
export const ConnectionProviderSchema = z.enum(CONNECTION_PROVIDERS);
export type ConnectionProvider = z.infer<typeof ConnectionProviderSchema>;
/** Calendar (visit booking) providers; a project books with one connection of any of them. */
/** `hubspot` = meeting links of a HubSpot CRM connection, only when its mapping turns `calendar.enabled` on (owner decision C2). */
export const CALENDAR_PROVIDERS = ['calendly', 'calcom', 'hubspot'] as const;
export type CalendarProvider = (typeof CALENDAR_PROVIDERS)[number];
export const isCalendarProvider = (p: string): p is CalendarProvider => (CALENDAR_PROVIDERS as readonly string[]).includes(p);

export const CONNECTION_STATUSES = ['pending', 'connected', 'needs_attention', 'disconnected', 'revoked'] as const;
export const ConnectionStatusSchema = z.enum(CONNECTION_STATUSES);
export type ConnectionStatus = z.infer<typeof ConnectionStatusSchema>;

/** `telnyx_line`: a Telnyx phone number used as a voice line (provider-neutral voice line; one provider per number). */
export const ENDPOINT_KINDS = ['whatsapp_number', 'aircall_line', 'telnyx_line'] as const;
export const EndpointKindSchema = z.enum(ENDPOINT_KINDS);
export type EndpointKind = z.infer<typeof EndpointKindSchema>;

export const WHATSAPP_MODES = ['coexistence', 'dedicated'] as const;
export const WhatsAppModeSchema = z.enum(WHATSAPP_MODES);
export type WhatsAppMode = z.infer<typeof WhatsAppModeSchema>;

export const CONVERSATION_MODES = ['ai_active', 'human_owned', 'paused', 'closed'] as const;
export const ConversationModeSchema = z.enum(CONVERSATION_MODES);
export type ConversationMode = z.infer<typeof ConversationModeSchema>;

export const DIRECTIONS = ['inbound', 'outbound'] as const;
export const DirectionSchema = z.enum(DIRECTIONS);
export type Direction = z.infer<typeof DirectionSchema>;

export const MESSAGE_AUTHORS = ['contact', 'ai', 'human', 'system'] as const;
export const MessageAuthorSchema = z.enum(MESSAGE_AUTHORS);
export type MessageAuthor = z.infer<typeof MessageAuthorSchema>;

export const DELIVERY_STATUSES = ['queued', 'provider_accepted', 'delivered', 'read', 'failed', 'unknown'] as const;
export const DeliveryStatusSchema = z.enum(DELIVERY_STATUSES);
export type DeliveryStatus = z.infer<typeof DeliveryStatusSchema>;

export const CALL_STATUSES = [
  'queued',
  'provider_accepted',
  'ringing',
  'answered',
  'completed',
  'no_answer',
  'busy',
  'voicemail',
  'failed',
  'uncertain',
] as const;
export const CallStatusSchema = z.enum(CALL_STATUSES);
export type CallStatus = z.infer<typeof CallStatusSchema>;

export const ANALYSIS_STATUSES = ['not_available', 'processing', 'ready', 'partial', 'failed'] as const;
export const AnalysisStatusSchema = z.enum(ANALYSIS_STATUSES);
export type AnalysisStatus = z.infer<typeof AnalysisStatusSchema>;

export const APPOINTMENT_STATUSES = [
  'proposed',
  'booking',
  'confirmed',
  'reschedule_requested',
  'cancel_requested',
  'cancelled',
  'completed',
  'no_show',
  'failed',
  'reconciliation_required',
] as const;
export const AppointmentStatusSchema = z.enum(APPOINTMENT_STATUSES);
export type AppointmentStatus = z.infer<typeof AppointmentStatusSchema>;

export const UNIT_AVAILABILITIES = ['available', 'held', 'reserved', 'sold', 'unavailable', 'unknown'] as const;
export const UnitAvailabilitySchema = z.enum(UNIT_AVAILABILITIES);
export type UnitAvailability = z.infer<typeof UnitAvailabilitySchema>;

export const INVENTORY_AUTHORITIES = ['crm', 'app', 'mixed'] as const;
export const InventoryAuthoritySchema = z.enum(INVENTORY_AUTHORITIES);
export type InventoryAuthority = z.infer<typeof InventoryAuthoritySchema>;

export const DOCUMENT_STATUSES = ['uploaded', 'processing', 'needs_review', 'published', 'failed', 'superseded'] as const;
export const DocumentStatusSchema = z.enum(DOCUMENT_STATUSES);
export type DocumentStatus = z.infer<typeof DocumentStatusSchema>;

export const VERSION_STATES = ['draft', 'tested', 'published', 'archived'] as const;
export const VersionStateSchema = z.enum(VERSION_STATES);
export type VersionState = z.infer<typeof VersionStateSchema>;

export const DEPLOYMENT_STATUSES = ['pending', 'applied', 'failed', 'action_required'] as const;
export const DeploymentStatusSchema = z.enum(DEPLOYMENT_STATUSES);
export type DeploymentStatus = z.infer<typeof DeploymentStatusSchema>;

export const ROUTINE_STATES = ['draft', 'test', 'live', 'paused'] as const;
export const RoutineStateSchema = z.enum(ROUTINE_STATES);
export type RoutineState = z.infer<typeof RoutineStateSchema>;

export const RECEIPT_STATUSES = ['accepted', 'processing', 'applied', 'skipped', 'failed', 'quarantined'] as const;
export const ReceiptStatusSchema = z.enum(RECEIPT_STATUSES);
export type ReceiptStatus = z.infer<typeof ReceiptStatusSchema>;

export const PERMISSION_STATUSES = ['granted', 'denied', 'revoked', 'unknown'] as const;
export const PermissionStatusSchema = z.enum(PERMISSION_STATUSES);
export type PermissionStatus = z.infer<typeof PermissionStatusSchema>;

export const CHANNEL_KINDS = ['whatsapp', 'voice'] as const;
export const ChannelKindSchema = z.enum(CHANNEL_KINDS);
export type ChannelKind = z.infer<typeof ChannelKindSchema>;

export const WEBHOOK_DELIVERY_STATUSES = ['pending', 'delivering', 'succeeded', 'failed', 'dead_letter'] as const;
export const WebhookDeliveryStatusSchema = z.enum(WEBHOOK_DELIVERY_STATUSES);
export type WebhookDeliveryStatus = z.infer<typeof WebhookDeliveryStatusSchema>;

export const HANDOFF_STATUSES = ['open', 'acknowledged', 'resolved', 'cancelled'] as const;
export const HandoffStatusSchema = z.enum(HANDOFF_STATUSES);
export type HandoffStatus = z.infer<typeof HandoffStatusSchema>;

export const CALENDLY_SCOPES = ['inherit_workspace', 'project_connection'] as const;
export const CalendlyScopeSchema = z.enum(CALENDLY_SCOPES);
export type CalendlyScope = z.infer<typeof CalendlyScopeSchema>;

export const PROJECT_STATUSES = ['draft', 'active', 'paused', 'archived'] as const;
export const ProjectStatusSchema = z.enum(PROJECT_STATUSES);
export type ProjectStatus = z.infer<typeof ProjectStatusSchema>;

/** Resumable onboarding checklist keys, in display order (PRD §5). */
export const SETUP_STEP_KEYS = [
  'workspace_identity',
  'first_project',
  'channels',
  'routing',
  'knowledge_inventory',
  'agent_schedule',
  'crm_calendar',
  'first_routine',
  'test_run',
  'activation',
] as const;
export const SetupStepKeySchema = z.enum(SETUP_STEP_KEYS);
export type SetupStepKey = z.infer<typeof SetupStepKeySchema>;

/**
 * Shared agent tool registry (PRD §8). Executable implementations, schemas and permissions live in code;
 * only model-facing wording is editable. Property reservation tools are deliberately absent.
 */
export const AGENT_TOOLS = [
  'get_project_context',
  'search_knowledge',
  'list_matching_units',
  'get_unit',
  'get_current_price',
  'get_availability',
  'book_appointment',
  'request_reschedule',
  'request_cancel',
  'send_approved_asset',
  'update_qualification',
  'request_handoff',
  'record_opt_out',
  'record_language',
  'report_abuse',
  /** Setter §02/§06.1/§06.9 (Wave 4B): the prospect agreed a time to be called («llámame a las 4»). */
  'schedule_commitment',
  /** Setter §09.4: a structured objection on the opportunity (category, the prospect's words, status, approved answer). */
  'record_objection',
  /** The prospect's answer to «¿nos confirmas tu asistencia?» (confirmed / declined / unsure); never cancels the visit. */
  'confirm_attendance',
  /** v1 «Preguntas por resolver»: a commercial question without an authorized source, grouped for the team. */
  'record_pending_question',
] as const;
export const AgentToolSchema = z.enum(AGENT_TOOLS);
export type AgentTool = z.infer<typeof AgentToolSchema>;

/** Outgoing webhook event catalogue (PRD §15). */
export const WEBHOOK_EVENT_TYPES = [
  'lead.created',
  'lead.qualified',
  'lead.disqualified',
  /** Any funnel stage change (fixed stage keys, source and evidence); drives the CRM stage writeback. */
  'lead.stage_changed',
  /** A new acquisition touchpoint (spec §08.6): ids only + channel; drives the optional CRM source/campaign writeback. */
  'lead.touch_recorded',
  /** Setter §09.4: a structured objection was recorded/updated on an opportunity (ids + category + status, no words). */
  'lead.objection_recorded',
  'conversation.handoff_requested',
  // Final handoff (spec §05.23): a person took the opportunity; LIA stops for good. CRM: «Atención humana».
  'conversation.handoff_completed',
  'conversation.summary_ready',
  'call.started',
  'call.completed',
  'call.failed',
  'call.analysis_ready',
  'appointment.confirmed',
  'appointment.rescheduled',
  'appointment.cancelled',
  // Booking loop (spec §10.5): a human/CRM reported Realizada / No show / Cancelada / Reprogramada (starts recovery
  // cadences on no_show/cancelled), or the end-of-day deadline passed without an outcome («resultado pendiente»).
  'appointment.outcome_recorded',
  'appointment.outcome_pending',
  // Owner decision C1: the opportunity was handed to the seller who receives the visit (1 h before). CRM: owner + note.
  'appointment.seller_handoff',
  // The prospect answered the attendance confirmation (confirmed / declined / unsure). CRM: note. Never cancels.
  'appointment.attendance_answered',
  // Setter §08.7 / §05.19: something needs a person's review (CRM «Cita agendada» without a real booking, incidents).
  // Drives the optional CRM task (crm mapping `tasks`). Ids + short Spanish text only.
  'opportunity.review_required',
  // v1 task-based operation: LIA's open task on an opportunity was opened / updated / closed (ids + change only).
  // Drives the CRM task on the deal (crm mapping `tasks.setter`).
  'opportunity.task_changed',
  // Incidents with a named owner (spec §05.19, §05.20): opened, escalated to the next owner, resolved. CRM: note.
  'incident.opened',
  'incident.escalated',
  'incident.resolved',
  'inventory.updated',
  'contact.opted_out',
  // Setter §09.9: the contact's preferred language changed (WhatsApp tool or a call); ids + language code only.
  'contact.language_changed',
  'integration.action_failed',
  // Data-subject erasure (ops): carries only ids, never PII, so receivers can delete their copy.
  'contact.erased',
] as const;
export const WebhookEventTypeSchema = z.enum(WEBHOOK_EVENT_TYPES);
export type WebhookEventType = z.infer<typeof WebhookEventTypeSchema>;

/** Normalized ingress event types accepted at /ingress/:connectionId/events (PRD §14). */
export const INGRESS_EVENT_TYPES = [
  'lead.created',
  'lead.updated',
  'lead.stage_changed',
  'inventory.unit.updated',
  'appointment.updated',
] as const;
export const IngressEventTypeSchema = z.enum(INGRESS_EVENT_TYPES);
export type IngressEventType = z.infer<typeof IngressEventTypeSchema>;

export const WEEKDAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;
export const WeekdaySchema = z.enum(WEEKDAYS);
export type Weekday = z.infer<typeof WeekdaySchema>;

/** What a model is used for. Each purpose can be assigned its own provider/model by the platform (PRD §8.2). */
export const MODEL_PURPOSES = ['whatsapp_agent', 'voice_agent', 'summaries', 'audits', 'vision', 'copilot'] as const;
export const ModelPurposeSchema = z.enum(MODEL_PURPOSES);
export type ModelPurpose = z.infer<typeof ModelPurposeSchema>;

export const MODEL_PROVIDER_KINDS = ['anthropic', 'openai', 'openai_compatible'] as const;
export const ModelProviderKindSchema = z.enum(MODEL_PROVIDER_KINDS);
export type ModelProviderKind = z.infer<typeof ModelProviderKindSchema>;
